import React from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import { useAppSelector } from "../../store/hooks";
import StatCard from "../../components/shared/StatCard";

const DEPT_COLORS: Record<string, string> = {
  Engineering: "#85D1DB",
  Sales: "#B6F2D1",
  Marketing: "#6BB8C3",
  Finance: "#22c55e",
  HR: "#f59e0b",
  Operations: "#f43f5e",
};

const getUpcomingHolidays = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentYear = today.getFullYear();
  const holidaysList = [
    { month: 0, date: 1, name: "New Year's Day" },
    { month: 4, date: 1, name: "Labor Day" },
    { month: 6, date: 4, name: "Independence Day" },
    { month: 9, date: 31, name: "Halloween" },
    { month: 10, date: 11, name: "Veterans Day" },
    { month: 11, date: 25, name: "Christmas Day" },
  ];
  let upcoming: any[] = [];
  for (let year = currentYear; year <= currentYear + 1; year++) {
    for (const h of holidaysList) {
      const hDate = new Date(year, h.month, h.date);
      if (hDate >= today) {
        upcoming.push({
          dateObj: hDate,
          name: h.name,
          displayFull: `${hDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
        });
      }
    }
  }
  upcoming.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  return upcoming.slice(0, 4);
};

const Dashboard: React.FC = () => {
  const departments = useAppSelector((s) => s.data.departments);
  const employees = useAppSelector((s) => s.data.employees);
  const attendance = useAppSelector((s) => s.data.attendance);
  const jobs = useAppSelector((s) => s.data.jobs);
  const leaveRequests = useAppSelector((s) => s.data.leaveRequests);
  const notifications = useAppSelector((s) => s.data.notifications);

  const chartData = React.useMemo(() => {
    if (!employees || employees.length === 0) return [];
    
    const deptMap: Record<string, number> = {};
    employees.forEach((emp) => {
      const deptName = emp.dept || 'Unassigned';
      deptMap[deptName] = (deptMap[deptName] || 0) + 1;
    });

    const total = employees.length;
    const fallbackColors = ['#85D1DB', '#B6F2D1', '#6BB8C3', '#22c55e', '#f59e0b', '#f43f5e', '#a855f7', '#ec4899'];

    return Object.keys(deptMap).map((dept, idx) => {
      const count = deptMap[dept];
      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
      return {
        name: dept,
        value: count,
        percentage,
        color: DEPT_COLORS[dept] || fallbackColors[idx % fallbackColors.length],
      };
    });
  }, [employees]);

  const dynamicStats = React.useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];
    const presentCount = attendance.filter(
      (a) =>
        a.date === todayStr && (a.status === "Present" || a.status === "Late"),
    ).length;
    const pendingLeaves = leaveRequests.filter(
      (l) => l.status === "Pending",
    ).length;
    const openPositions = jobs.length;

    return [
      {
        label: "Total Employees",
        value: employees.length.toString(),
        icon: "People",
        delta: "Active Directory",
        deltaType: "neutral" as const,
        color: "#85D1DB",
        iconBg: "rgba(133,209,219,0.1)",
      },
      {
        label: "Present Today",
        value: presentCount.toString(),
        icon: "CheckCircle",
        delta: "Based on check-ins",
        deltaType: "neutral" as const,
        color: "#16a34a",
        iconBg: "#dcfce7",
      },
      {
        label: "Open Positions",
        value: openPositions.toString(),
        icon: "Work",
        delta: "Active recruitments",
        deltaType: "neutral" as const,
        color: "#6BB8C3",
        iconBg: "#C9FDF2",
      },
      {
        label: "Pending Leaves",
        value: pendingLeaves.toString(),
        icon: "PendingActions",
        delta: pendingLeaves > 0 ? "Requires action" : "All clear",
        deltaType: pendingLeaves > 0 ? ("down" as const) : ("neutral" as const),
        color: "#d97706",
        iconBg: "#fef3c7",
      },
    ];
  }, [employees, attendance, jobs, leaveRequests]);

  const dynamicActivities = React.useMemo(() => {
    const activities: Array<{
      icon: string;
      color: string;
      bg: string;
      title: string;
      sub: string;
      timestamp: number;
    }> = [];
    const now = new Date().getTime();

    // 1. Existing Notifications
    if (notifications && notifications.length > 0) {
      notifications.forEach((n, i) => {
        let icon = "🔔";
        let color = "#6BB8C3";
        let bg = "#C9FDF2";
        let title = "System Alert";
        if (n.type === "leave") {
          icon = "📋";
          color = "#85D1DB";
          bg = "rgba(133,209,219,0.1)";
          title = "Leave Request";
        } else if (n.type === "system") {
          icon = "⚙️";
          color = "#d97706";
          bg = "#fef3c7";
        }
        activities.push({
          icon,
          color,
          bg,
          title,
          sub: n.message,
          timestamp: new Date(n.time || "").getTime() || now - i * 1000,
        });
      });
    }

    // 2. Recent Leave Requests
    if (leaveRequests && leaveRequests.length > 0) {
      leaveRequests.slice(0, 3).forEach((l, idx) => {
        activities.push({
          icon: "📅",
          color: l.status === "Approved" ? "#16a34a" : l.status === "Rejected" ? "#dc2626" : "#d97706",
          bg: l.status === "Approved" ? "#dcfce7" : l.status === "Rejected" ? "#fee2e2" : "#fef3c7",
          title: `Leave ${l.status}`,
          sub: `${l.name} requested ${l.type} leave`,
          // Fake timestamp for ordering if created date not available
          timestamp: now - (idx + 1) * 60000,
        });
      });
    }

    // 3. Recent Attendance (Today)
    const todayStr = new Date().toISOString().split("T")[0];
    if (attendance && attendance.length > 0) {
      attendance
        .filter((a) => a.date === todayStr && a.clockIn && a.clockIn !== "—")
        .slice(0, 3)
        .forEach((a, idx) => {
          activities.push({
            icon: "⏱️",
            color: "#22c55e",
            bg: "#dcfce7",
            title: "Checked In",
            sub: `${a.name} checked in at ${a.clockIn}`,
            timestamp: (a as any).rawCheckIn
              ? new Date((a as any).rawCheckIn).getTime()
              : now - idx * 30000,
          });
        });
    }

    // 4. New Employees
    if (employees && employees.length > 0) {
      const recentEmployees = [...employees].reverse().slice(0, 2);
      recentEmployees.forEach((e, idx) => {
        activities.push({
          icon: "👋",
          color: "#a855f7",
          bg: "#f3e8ff",
          title: "New Team Member",
          sub: `${e.name} joined as ${e.role || "Employee"}`,
          timestamp: e.joined ? new Date(e.joined).getTime() : now - 86400000 - idx * 1000,
        });
      });
    }

    return activities
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5);
  }, [notifications, attendance, leaveRequests, employees]);

  const dynamicLeaveBreakdown = React.useMemo(() => {
    const pending = leaveRequests.filter((l) => l.status === "Pending").length;
    const approved = leaveRequests.filter(
      (l) => l.status === "Approved",
    ).length;
    const rejected = leaveRequests.filter(
      (l) => l.status === "Rejected",
    ).length;

    const total = leaveRequests.length || 1;
    const casual = leaveRequests.filter((l) => l.type === "Casual").length;
    const sick = leaveRequests.filter((l) => l.type === "Sick").length;
    const earned = leaveRequests.filter((l) => l.type === "Earned").length;

    return {
      summary: [
        { n: pending, l: "Pending", c: "#d97706" },
        { n: approved, l: "Approved", c: "#16a34a" },
        { n: rejected, l: "Rejected", c: "#dc2626" },
      ],
      breakdown: [
        {
          type: "Casual",
          pct: Math.round((casual / total) * 100),
          color: "#85D1DB",
        },
        {
          type: "Sick",
          pct: Math.round((sick / total) * 100),
          color: "#dc2626",
        },
        {
          type: "Earned",
          pct: Math.round((earned / total) * 100),
          color: "#16a34a",
        },
      ],
    };
  }, [leaveRequests]);

  const dynamicEvents = React.useMemo(() => {
    const evs = [];
    const holidays = getUpcomingHolidays();
    for (const h of holidays) {
      evs.push({
        icon: "🎉",
        title: h.name,
        sub: h.displayFull + " • Holiday",
        dateObj: h.dateObj,
      });
    }
    return evs.slice(0, 4);
  }, []);

  return (
    <Box>
      <SEO title="Dashboard" noindex />
      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {dynamicStats.map((s) => (
          <Grid item xs={12} sm={6} lg={3} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      {/* Row 2 */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
                Headcount by Department
              </Typography>
              <ResponsiveContainer width="100%" height={240}>
                {chartData.length > 0 ? (
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: any, name: any, props: any) => [
                        `${value} Employees (${props.payload.percentage}%)`,
                        name,
                      ]}
                      contentStyle={{
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        fontSize: 12,
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36} 
                      iconType="circle"
                      wrapperStyle={{ fontSize: 12, color: '#64748b' }}
                    />
                  </PieChart>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
                      No data available
                    </Typography>
                  </Box>
                )}
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
                Recent Activity
              </Typography>
              {dynamicActivities.length > 0 ? (
                dynamicActivities.map((a, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        background: a.bg,
                        color: a.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        flexShrink: 0,
                      }}
                    >
                      {a.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                        {a.title}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}
                      >
                        {a.sub}
                      </Typography>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography
                  sx={{ fontSize: 13, color: "#94a3b8", fontStyle: "italic" }}
                >
                  No recent activity to show.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Row 3 */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
                Leave Overview
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3,1fr)",
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2.5,
                }}
              >
                {dynamicLeaveBreakdown.summary.map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 1.8,
                      textAlign: "center",
                      borderRight: i < 2 ? "1px solid #e5e7eb" : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: item.c,
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      {item.n}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                      {item.l}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {dynamicLeaveBreakdown.breakdown.map((l) => (
                <Box key={l.type} sx={{ mb: 1.2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 12 }}>{l.type}</Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#94a3b8",
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      {l.pct}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={l.pct}
                    sx={{
                      height: 6,
                      borderRadius: 99,
                      backgroundColor: "#f1f5f9",
                      "& .MuiLinearProgress-bar": {
                        background: l.color,
                        borderRadius: 99,
                      },
                    }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
                Upcoming Events
              </Typography>
              {dynamicEvents.map((e, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    mb: 1.8,
                  }}
                >
                  <Typography sx={{ fontSize: 20, flexShrink: 0, mt: 0.2 }}>
                    {e.icon}
                  </Typography>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                      {e.title}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}
                    >
                      {e.sub}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dept Table */}
      <Card>
        <CardContent sx={{ p: "0 !important" }}>
          <Box sx={{ p: "14px 20px", borderBottom: "1px solid #e5e7eb" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              Department Summary
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Department",
                    "Head",
                    "Employees",
                    "Avg Salary",
                    "Budget Used",
                    "Open Roles",
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {departments.map((d) => (
                  <TableRow
                    key={d.id}
                    sx={{
                      transition: "background 0.15s",
                      "&:hover": { background: "#fafbff" },
                    }}
                  >
                    <TableCell
                      sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: DEPT_COLORS[d.name],
                            flexShrink: 0,
                          }}
                        />
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                          {d.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid #f1f5f9",
                        fontSize: 13,
                        color: "#64748b",
                      }}
                    >
                      {d.head}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid #f1f5f9",
                        fontSize: 13,
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      {d.employees}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid #f1f5f9",
                        fontSize: 13,
                        fontFamily: "'JetBrains Mono',monospace",
                      }}
                    >
                      ₹{d.avgSalary.toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell
                      sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={d.budgetUsed}
                          sx={{
                            width: 80,
                            height: 6,
                            borderRadius: 99,
                            backgroundColor: "#e2e8f0",
                            "& .MuiLinearProgress-bar": {
                              background: "#85D1DB",
                              borderRadius: 99,
                            },
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontFamily: "'JetBrains Mono',monospace",
                            color: "#64748b",
                          }}
                        >
                          {d.budgetUsed}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ py: 1.5, px: 2, borderBottom: "1px solid #f1f5f9" }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 26,
                          height: 26,
                          borderRadius: 1,
                          background:
                            d.openRoles > 0
                              ? "rgba(133,209,219,0.1)"
                              : "#f1f5f9",
                          color: d.openRoles > 0 ? "#6BB8C3" : "#94a3b8",
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: "'JetBrains Mono',monospace",
                        }}
                      >
                        {d.openRoles}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
